import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import "./css/SuggestionsPage.css";
import {
  createSuggestion,
  deleteSuggestionByAdmin,
  getAllPostSuggestionsByAdmin,
  updateSuggestionByAdmin,
} from "../api/suggestionsApi";
import {
  FiPlus,
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiX,
  FiPhone,
  FiTruck,
  FiUser,
  FiMessageSquare,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiThumbsUp,
  FiThumbsDown,
  FiUsers,
  FiClipboard,
  FiAlertCircle,
  FiBarChart2,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
} from "recharts";
import AccessControl from "../components/AccessControl";

const LIMIT = 10;

export default function SuggestionsPage() {
  const data = localStorage.getItem("admin");
  const userData = data ? JSON.parse(data) : null;

  // ─── Data States ──────────────────────────────────────────
  const [suggestions, setSuggestions] = useState([]);
  // ✅ FIX 1: allSuggestions remove panniten (unused variable)

  const [stats, setStats] = useState({
    total: 0,
    accepted: 0,
    rejected: 0,
    pending: 0,
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });

  // ─── UI States ────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState({
    show: false,
    id: null,
    action: null,
  });
  const [editingId, setEditingId] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [errors, setErrors] = useState({});
  const [chartView, setChartView] = useState("status");

  // ─── Search / Filter ──────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const debounceRef = useRef(null);

  // ─── Form ─────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    driverName: "",
    vehicleNumber: "",
    suggestion: "",
    mobileNumber: "",
    createdBy: userData?.name || "",
    isAccepted: null,
  });

  // ──────────────────────────────────────────────────────────
  //  TOAST
  // ──────────────────────────────────────────────────────────
  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  // ──────────────────────────────────────────────────────────
  //  FETCH
  //  ✅ FIX 2 & 3: No state as default params → pure function
  //  useCallback deps [] → stable reference → no infinite loop
  // ──────────────────────────────────────────────────────────
  const fetchSuggestions = useCallback(async (page, search, status) => {
    try {
      setLoading(true);

      const statusMap = {
        all: "",
        accepted: "accepted",
        rejected: "rejected",
        pending: "pending",
      };

      const payload = {
        n_page: page,
        n_limit: LIMIT,
        c_search_term: (search || "").trim(),
        c_status: statusMap[status] || "",
      };

      const res = await getAllPostSuggestionsByAdmin(payload);

      if (!res.success) {
        setSuggestions([]);
        return;
      }

      const normalized = (res.suggestions || []).map((s) => ({
        ...s,
        mobileNumber: String(s.mobileNumber || ""),
      }));

      setSuggestions(normalized);
      setPagination(
        res.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalCount: 0,
          hasNextPage: false,
          hasPrevPage: false,
        },
      );

      if (res.stats) {
        setStats(res.stats);
      }
    } catch (err) {
      console.log(err, "<<< err fetching suggestions");
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []); // ✅ Empty deps ok - no state inside, params ah pass panrom

  // ─── Initial Load ─────────────────────────────────────────
  // ✅ FIX 4: fetchSuggestions dep add panniten
  useEffect(() => {
    fetchSuggestions(1, "", "all");
  }, [fetchSuggestions]);

  // ─── Search Debounce (400ms) ──────────────────────────────
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(1, value, filterStatus); // ✅ filterStatus explicitly pass
    }, 400);
  };

  // ─── Filter Tab Change ────────────────────────────────────
  const handleFilterChange = (status) => {
    setFilterStatus(status);
    setCurrentPage(1);
    fetchSuggestions(1, searchTerm, status); // ✅ searchTerm explicitly pass
  };

  // ─── Page Change ─────────────────────────────────────────
  const handlePageChange = (page) => {
    if (page < 1 || page > pagination.totalPages) return;
    setCurrentPage(page);
    fetchSuggestions(page, searchTerm, filterStatus); // ✅ both explicitly pass
    window.scrollTo({ top: 400, behavior: "smooth" });
  };

  // ──────────────────────────────────────────────────────────
  //  INPUT HANDLERS
  // ──────────────────────────────────────────────────────────
  const handleVehicleChange = (e) => {
    const cleaned = e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    setFormData({ ...formData, vehicleNumber: cleaned });
    if (errors.vehicleNumber) setErrors({ ...errors, vehicleNumber: "" });
  };

  const handleMobileChange = (e) => {
    const cleaned = e.target.value.replace(/[^0-9]/g, "").slice(0, 10);
    setFormData({ ...formData, mobileNumber: cleaned });
    if (errors.mobileNumber) setErrors({ ...errors, mobileNumber: "" });
  };

  const capitalizeFirstLetter = (str) =>
    str
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

  const handleDriverNameChange = (e) => {
    const cleaned = e.target.value.replace(/[^a-zA-Z\s]/g, "");
    setFormData({
      ...formData,
      driverName: capitalizeFirstLetter(cleaned),
    });
    if (errors.driverName) setErrors({ ...errors, driverName: "" });
  };

  const handleCreatedByChange = (e) => {
    const cleaned = e.target.value.replace(/[^a-zA-Z\s]/g, "");
    setFormData({
      ...formData,
      createdBy: capitalizeFirstLetter(cleaned),
    });
    if (errors.createdBy) setErrors({ ...errors, createdBy: "" });
  };

  // ──────────────────────────────────────────────────────────
  //  VALIDATION
  // ──────────────────────────────────────────────────────────
  const validateForm = () => {
    const newErrors = {};
    if (!formData.driverName.trim())
      newErrors.driverName = "Driver name is required";
    else if (formData.driverName.trim().length < 2)
      newErrors.driverName = "Min 2 characters";

    if (!formData.vehicleNumber.trim())
      newErrors.vehicleNumber = "Vehicle number is required";
    else if (formData.vehicleNumber.length < 4)
      newErrors.vehicleNumber = "Too short";

    if (!formData.mobileNumber.trim())
      newErrors.mobileNumber = "Mobile number is required";
    else if (formData.mobileNumber.length !== 10)
      newErrors.mobileNumber = "Must be 10 digits";
    else if (!/^[6-9]/.test(formData.mobileNumber))
      newErrors.mobileNumber = "Must start with 6-9";

    if (!formData.suggestion.trim())
      newErrors.suggestion = "Suggestion is required";

    if (!formData.createdBy.trim())
      newErrors.createdBy = "Created by is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ──────────────────────────────────────────────────────────
  //  CRUD
  // ──────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      showToast("Please fix the errors!", "error");
      return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        await updateSuggestionByAdmin(editingId, formData);
        showToast("Suggestion updated successfully!");
      } else {
        await createSuggestion(formData);
        showToast("Suggestion added successfully!");
      }
      resetForm();
      fetchSuggestions(currentPage, searchTerm, filterStatus); // ✅ explicit
    } catch (err) {
      showToast("Operation failed!", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecision = async (id, isAccepted) => {
    setDeciding(true);
    try {
      await updateSuggestionByAdmin(id, { isAccepted });

      const oldItem = suggestions.find((s) => s._id === id);

      // ✅ Update local suggestions
      setSuggestions((prev) =>
        prev.map((s) => (s._id === id ? { ...s, isAccepted } : s)),
      );

      // ✅ Update stats locally
      setStats((prev) => {
        const updated = { ...prev };
        if (oldItem?.isAccepted === true)
          updated.accepted = Math.max(0, updated.accepted - 1);
        else if (oldItem?.isAccepted === false)
          updated.rejected = Math.max(0, updated.rejected - 1);
        else updated.pending = Math.max(0, updated.pending - 1);

        if (isAccepted === true) updated.accepted += 1;
        else if (isAccepted === false) updated.rejected += 1;
        else updated.pending += 1;
        return updated;
      });

      showToast(
        isAccepted ? "Suggestion Approved!" : "Suggestion Rejected!",
        isAccepted ? "success" : "warning",
      );
      setShowConfirm({ show: false, id: null, action: null });
    } catch (err) {
      showToast("Failed to update decision!", "error");
    } finally {
      setDeciding(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteSuggestionByAdmin(id);
      showToast("Suggestion deleted!");
      const newPage =
        suggestions.length === 1 && currentPage > 1
          ? currentPage - 1
          : currentPage;
      setCurrentPage(newPage);
      fetchSuggestions(newPage, searchTerm, filterStatus); // ✅ explicit
    } catch (err) {
      showToast("Failed to delete!", "error");
    }
  };

  const handleEdit = (s) => {
    setFormData({
      driverName: s.driverName,
      vehicleNumber: s.vehicleNumber,
      suggestion: s.suggestion,
      mobileNumber: String(s.mobileNumber || ""),
      createdBy: s.createdBy,
      isAccepted:
        s.isAccepted === true ? true : s.isAccepted === false ? false : null,
    });
    setEditingId(s._id);
    setErrors({});
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      driverName: "",
      vehicleNumber: "",
      suggestion: "",
      mobileNumber: "",
      createdBy: userData?.name || "",
      isAccepted: null,
    });
    setEditingId(null);
    setErrors({});
    setShowModal(false);
  };

  // ──────────────────────────────────────────────────────────
  //  PAGINATION PAGE NUMBERS
  // ──────────────────────────────────────────────────────────
  const getPageNumbers = () => {
    const { currentPage: cp, totalPages: tp } = pagination;
    if (tp <= 1) return [];

    const pages = [];

    if (tp <= 5) {
      for (let i = 1; i <= tp; i++) pages.push(i);
    } else {
      pages.push(1);
      let start = Math.max(2, cp - 1);
      let end = Math.min(tp - 1, cp + 1);
      if (cp <= 3) {
        start = 2;
        end = Math.min(4, tp - 1);
      }
      if (cp >= tp - 2) {
        start = Math.max(2, tp - 3);
        end = tp - 1;
      }
      if (start > 2) pages.push("...");
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < tp - 1) pages.push("...");
      if (tp > 1) pages.push(tp);
    }
    return pages;
  };

  // ──────────────────────────────────────────────────────────
  //  CHART DATA
  // ──────────────────────────────────────────────────────────
  const statusChartData = useMemo(
    () => [
      { name: "Accepted", value: stats.accepted, color: "#1D9E75" },
      { name: "Rejected", value: stats.rejected, color: "#D85A30" },
      { name: "Pending", value: stats.pending, color: "#E6A817" },
    ],
    [stats],
  );

  const driverChartData = useMemo(() => {
    const map = {};
    suggestions.forEach((s) => {
      const name = s.driverName || "Unknown";
      if (!map[name])
        map[name] = { name, accepted: 0, rejected: 0, pending: 0, total: 0 };
      map[name].total += 1;
      if (s.isAccepted === true) map[name].accepted += 1;
      else if (s.isAccepted === false) map[name].rejected += 1;
      else map[name].pending += 1;
    });
    return Object.values(map)
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);
  }, [suggestions]);

  const dailyChartData = useMemo(() => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split("T")[0];
      const dayLabel = date.toLocaleDateString("en-US", {
        weekday: "short",
        day: "numeric",
      });
      const dayItems = suggestions.filter(
        (s) =>
          s.createdAt &&
          new Date(s.createdAt).toISOString().split("T")[0] === dateStr,
      );
      days.push({
        name: dayLabel,
        accepted: dayItems.filter((s) => s.isAccepted === true).length,
        rejected: dayItems.filter((s) => s.isAccepted === false).length,
        pending: dayItems.filter(
          (s) => s.isAccepted === null || s.isAccepted === undefined,
        ).length,
      });
    }
    return days;
  }, [suggestions]);

  // ──────────────────────────────────────────────────────────
  //  SUB COMPONENTS
  // ──────────────────────────────────────────────────────────
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="sg-chart-tooltip">
        <p className="sg-tooltip-label">{label}</p>
        {payload.map((entry, i) => (
          <p
            key={i}
            className="sg-tooltip-value"
            style={{ color: entry.color || entry.fill }}
          >
            {entry.name}: <strong>{entry.value}</strong>
          </p>
        ))}
      </div>
    );
  };

  const StatusBadge = ({ isAccepted }) => {
    if (isAccepted === true)
      return (
        <span className="badge badge-green">
          <FiCheckCircle size={12} style={{ marginRight: 4 }} />
          Accepted
        </span>
      );
    if (isAccepted === false)
      return (
        <span className="badge badge-red">
          <FiXCircle size={12} style={{ marginRight: 4 }} />
          Rejected
        </span>
      );
    return (
      <span className="badge badge-amber">
        <FiClock size={12} style={{ marginRight: 4 }} />
        Pending
      </span>
    );
  };

  const ActionButtons = ({ item }) => {
    const isAccepted = item.isAccepted;
    return (
      <div className="sg-mobile-card-actions">
        {userData?.role === "SUPERADMIN" || userData?.role === "ADMIN" ? (
          <>
            <button
              className={`sg-action-btn sg-btn-approve ${isAccepted === true ? "sg-btn-disabled" : ""}`}
              disabled={isAccepted === true}
              title={isAccepted === true ? "Already Approved" : "Approve"}
              onClick={() =>
                setShowConfirm({ show: true, id: item._id, action: "approve" })
              }
            >
              <FiThumbsUp size={14} />
            </button>
            <button
              className={`sg-action-btn sg-btn-reject ${isAccepted === false ? "sg-btn-disabled" : ""}`}
              disabled={isAccepted === false}
              title={isAccepted === false ? "Already Rejected" : "Reject"}
              onClick={() =>
                setShowConfirm({ show: true, id: item._id, action: "reject" })
              }
            >
              <FiThumbsDown size={14} />
            </button>
            <button
              className="sg-action-btn sg-btn-edit"
              title="Edit"
              onClick={() => handleEdit(item)}
            >
              <FiEdit2 size={14} />
            </button>
            <button
              className="sg-action-btn sg-btn-delete"
              title="Delete"
              onClick={() => handleDelete(item._id)}
            >
              <FiTrash2 size={14} />
            </button>
          </>
        ) : (
          <>
            <button
              className="sg-action-btn sg-btn-edit"
              title="Edit"
              onClick={() => handleEdit(item)}
            >
              <FiEdit2 size={14} />
            </button>
          </>
        )}
      </div>
    );
  };

  // ──────────────────────────────────────────────────────────
  //  RENDER
  // ──────────────────────────────────────────────────────────
  return (
    <div>
<AccessControl
        allowedRoles={["SUPERADMIN", "ADMIN","HR", "NSM", "RSM", "ASM", "TSO"]}
        redirectTo="/404"
      />
      {/* Toast */}
      {toast.show && (
        <div className={`sg-toast sg-toast-${toast.type}`}>
          <span>
            {toast.type === "success" ? (
              <FiCheckCircle size={16} />
            ) : toast.type === "warning" ? (
              <FiAlertCircle size={16} />
            ) : (
              <FiXCircle size={16} />
            )}
          </span>
          <span>{toast.message}</span>
        </div>
      )}

      {/* Page Header */}
      <div className="sg-page-header section">
        <div>
          <h2
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: "#1a1a1a",
              margin: 0,
            }}
          >
            Driver Suggestions
          </h2>
          <p style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
            Manage and review driver suggestions
          </p>
        </div>
        <button
          className="btn btn-success"
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
        >
          <FiPlus size={14} /> New Suggestion
        </button>
      </div>

      {/* Stat Cards */}
      <div className="stat-grid section">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "#E6F1FB" }}>
            <FiClipboard size={18} color="#185FA5" />
          </div>
          <div className="stat-label">Total Suggestions</div>
          <div className="stat-value">{stats.total}</div>
          <div className="stat-change up">All time records</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "#E1F5EE" }}>
            <FiCheckCircle size={18} color="#0F6E56" />
          </div>
          <div className="stat-label">Accepted</div>
          <div className="stat-value" style={{ color: "#0F6E56" }}>
            {stats.accepted}
          </div>
          <div className="stat-change up">Approved suggestions</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "#FCEBEB" }}>
            <FiXCircle size={18} color="#A32D2D" />
          </div>
          <div className="stat-label">Rejected</div>
          <div className="stat-value" style={{ color: "#A32D2D" }}>
            {stats.rejected}
          </div>
          <div className="stat-change down">Declined suggestions</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "#FAEEDA" }}>
            <FiClock size={18} color="#854F0B" />
          </div>
          <div className="stat-label">Pending</div>
          <div className="stat-value" style={{ color: "#854F0B" }}>
            {stats.pending}
          </div>
          <div className="stat-change neutral">Awaiting review</div>
        </div>
      </div>

      {/* Charts */}
      {stats.total > 0 && (
        <div className="grid-2 section">
          <div className="card">
            <div className="card-header">
              <span className="card-title">
                <FiBarChart2 size={14} style={{ marginRight: 6 }} />
                {chartView === "status"
                  ? "Status Overview"
                  : chartView === "driver"
                    ? "Driver-wise"
                    : "Daily Trend"}
              </span>
              <div className="sg-chart-tabs">
                {["status", "driver", "daily"].map((v) => (
                  <button
                    key={v}
                    className={`sg-chart-tab ${chartView === v ? "sg-chart-tab-active" : ""}`}
                    onClick={() => setChartView(v)}
                  >
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="chart-wrap" style={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                {chartView === "status" ? (
                  <BarChart data={statusChartData} barSize={50}>
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11, fill: "#888" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#888" }}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      content={<CustomTooltip />}
                      cursor={{ fill: "#f5f5f0" }}
                    />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                      {statusChartData.map((e, i) => (
                        <Cell key={i} fill={e.color} />
                      ))}
                    </Bar>
                  </BarChart>
                ) : chartView === "driver" ? (
                  <BarChart data={driverChartData} barSize={24}>
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 10, fill: "#888" }}
                      axisLine={false}
                      tickLine={false}
                      interval={0}
                      angle={-20}
                      textAnchor="end"
                      height={50}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#888" }}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      content={<CustomTooltip />}
                      cursor={{ fill: "#f5f5f0" }}
                    />
                    <Bar
                      dataKey="accepted"
                      fill="#1D9E75"
                      stackId="a"
                      name="Accepted"
                    />
                    <Bar
                      dataKey="rejected"
                      fill="#D85A30"
                      stackId="a"
                      name="Rejected"
                    />
                    <Bar
                      dataKey="pending"
                      fill="#E6A817"
                      stackId="a"
                      radius={[4, 4, 0, 0]}
                      name="Pending"
                    />
                  </BarChart>
                ) : (
                  <BarChart data={dailyChartData} barSize={20}>
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 10, fill: "#888" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: "#888" }}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      content={<CustomTooltip />}
                      cursor={{ fill: "#f5f5f0" }}
                    />
                    <Bar
                      dataKey="accepted"
                      fill="#1D9E75"
                      stackId="a"
                      name="Accepted"
                    />
                    <Bar
                      dataKey="rejected"
                      fill="#D85A30"
                      stackId="a"
                      name="Rejected"
                    />
                    <Bar
                      dataKey="pending"
                      fill="#E6A817"
                      stackId="a"
                      radius={[4, 4, 0, 0]}
                      name="Pending"
                    />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-title">
                <FiUsers size={14} style={{ marginRight: 6 }} />
                Distribution & Top Drivers
              </span>
            </div>
            <div style={{ height: 130 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusChartData.filter((d) => d.value > 0)}
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={55}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {statusChartData.map((e, i) => (
                      <Cell key={i} fill={e.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    iconType="circle"
                    iconSize={8}
                    wrapperStyle={{ fontSize: 11 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div style={{ marginTop: 8 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "#888",
                  marginBottom: 8,
                  textTransform: "uppercase",
                }}
              >
                Top Drivers
              </div>
              {driverChartData.slice(0, 4).map((d) => (
                <div key={d.name} className="sg-top-driver-row">
                  <div className="sg-top-driver-left">
                    <div
                      className="avatar"
                      style={{
                        width: 28,
                        height: 28,
                        fontSize: 11,
                        background: `hsl(${(d.name?.charCodeAt(0) || 0) * 15}, 50%, 85%)`,
                        color: `hsl(${(d.name?.charCodeAt(0) || 0) * 15}, 60%, 35%)`,
                      }}
                    >
                      {d.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#1a1a1a",
                        }}
                      >
                        {d.name}
                      </div>
                      <div style={{ fontSize: 10, color: "#888" }}>
                        {d.total} suggestion{d.total > 1 ? "s" : ""}
                      </div>
                    </div>
                  </div>
                  <div className="sg-top-driver-stats">
                    <span className="sg-mini-badge sg-mini-green">
                      {d.accepted}
                    </span>
                    <span className="sg-mini-badge sg-mini-red">
                      {d.rejected}
                    </span>
                    <span className="sg-mini-badge sg-mini-amber">
                      {d.pending}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filter + Search */}
      <div className="card section">
        <div className="sg-toolbar">
          <div className="filter-tabs">
            {["all", "accepted", "rejected", "pending"].map((f) => (
              <button
                key={f}
                className={`filter-tab ${filterStatus === f ? "active" : ""}`}
                onClick={() => handleFilterChange(f)}
              >
                {f === "all" && (
                  <>
                    <FiUsers size={12} /> All
                  </>
                )}
                {f === "accepted" && (
                  <>
                    <FiCheckCircle size={12} /> Accepted
                  </>
                )}
                {f === "rejected" && (
                  <>
                    <FiXCircle size={12} /> Rejected
                  </>
                )}
                {f === "pending" && (
                  <>
                    <FiClock size={12} /> Pending
                  </>
                )}
                <span className="sg-filter-count">
                  {f === "all" ? stats.total : (stats[f] ?? 0)}
                </span>
              </button>
            ))}
          </div>
          <div className="search-wrap">
            <FiSearch
              size={16}
              style={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#aaa",
              }}
            />
            <input
              className="search-input"
              type="text"
              placeholder="Search suggestions..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
        </div>
      </div>

      {/* Table / Cards */}
      <div className="card section">
        <div className="card-header">
          <span className="card-title">All Suggestions</span>
          <span style={{ fontSize: 12, color: "#888" }}>
            {loading
              ? "Loading..."
              : pagination.totalCount === 0
                ? "No results"
                : `Showing ${(pagination.currentPage - 1) * LIMIT + 1}–${Math.min(
                    pagination.currentPage * LIMIT,
                    pagination.totalCount,
                  )} of ${pagination.totalCount}`}
          </span>
        </div>

        {loading ? (
          <div className="sg-loading">
            <div
              className="spinner"
              style={{
                width: 24,
                height: 24,
                borderColor: "#1d9e75",
                borderTopColor: "transparent",
              }}
            ></div>
            <p style={{ marginTop: 12, color: "#888", fontSize: 13 }}>
              Loading suggestions...
            </p>
          </div>
        ) : suggestions.length === 0 ? (
          <div className="empty-state">
            <FiMessageSquare size={48} color="#ccc" />
            <p style={{ fontWeight: 600, color: "#555", marginTop: 8 }}>
              No suggestions found
            </p>
            <p style={{ fontSize: 12, color: "#999" }}>
              Try adjusting your search or filter
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="table-wrap sg-desktop-table">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Driver</th>
                    <th>Vehicle No.</th>
                    <th>Suggestion</th>
                    <th>Mobile</th>
                    <th>Created By</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {suggestions.map((s, i) => {
                    const rowNum = (pagination.currentPage - 1) * LIMIT + i + 1;
                    return (
                      <tr key={s._id}>
                        <td>
                          <span className="sg-row-num">{rowNum}</span>
                        </td>
                        <td>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                            }}
                          >
                            <div
                              className="avatar"
                              style={{
                                background: `hsl(${(s.driverName?.charCodeAt(0) || 0) * 15}, 50%, 85%)`,
                                color: `hsl(${(s.driverName?.charCodeAt(0) || 0) * 15}, 60%, 35%)`,
                              }}
                            >
                              {s.driverName?.charAt(0)?.toUpperCase() || "?"}
                            </div>
                            <span style={{ fontWeight: 600, fontSize: 13 }}>
                              {s.driverName}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className="sg-vehicle-badge">
                            {s.vehicleNumber}
                          </span>
                        </td>
                        <td>
                          <span
                            className="sg-suggestion-text"
                            title={s.suggestion}
                          >
                            {s.suggestion}
                          </span>
                        </td>
                        <td style={{ whiteSpace: "nowrap", color: "#555" }}>
                          <FiPhone size={12} style={{ marginRight: 4 }} />
                          {s.mobileNumber}
                        </td>
                        <td style={{ color: "#888" }}>{s.createdBy}</td>
                        <td>
                          <StatusBadge isAccepted={s.isAccepted} />
                        </td>
                        <td>
                          <ActionButtons item={s} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="sg-mobile-cards">
              {suggestions.map((s, i) => {
                const rowNum = (pagination.currentPage - 1) * LIMIT + i + 1;
                return (
                  <div className="sg-mobile-card" key={s._id}>
                    <div className="sg-mobile-card-header">
                      <div className="sg-mobile-card-driver">
                        <div
                          className="avatar"
                          style={{
                            background: `hsl(${(s.driverName?.charCodeAt(0) || 0) * 15}, 50%, 85%)`,
                            color: `hsl(${(s.driverName?.charCodeAt(0) || 0) * 15}, 60%, 35%)`,
                          }}
                        >
                          {s.driverName?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                        <div className="sg-mobile-driver-info">
                          <div className="sg-mobile-driver-name">
                            {s.driverName}
                          </div>
                          <div className="sg-mobile-driver-vehicle">
                            <span className="sg-vehicle-badge">
                              {s.vehicleNumber}
                            </span>
                          </div>
                        </div>
                      </div>
                      <StatusBadge isAccepted={s.isAccepted} />
                    </div>
                    <div className="sg-mobile-card-body">
                      <div className="sg-mobile-suggestion-label">
                        Suggestion
                      </div>
                      <div className="sg-mobile-suggestion-text">
                        {s.suggestion}
                      </div>
                    </div>
                    <div className="sg-mobile-card-meta">
                      <div className="sg-mobile-meta-item">
                        <span className="sg-mobile-meta-label">Mobile</span>
                        <span className="sg-mobile-meta-value">
                          <FiPhone size={12} /> {s.mobileNumber}
                        </span>
                      </div>
                      <div className="sg-mobile-meta-item">
                        <span className="sg-mobile-meta-label">Created By</span>
                        <span className="sg-mobile-meta-value">
                          {s.createdBy}
                        </span>
                      </div>
                    </div>
                    <div className="sg-mobile-card-footer">
                      <span
                        className="sg-row-num"
                        style={{ fontSize: 10, color: "#bbb" }}
                      >
                        #{rowNum}
                      </span>
                      <ActionButtons item={s} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* PAGINATION */}
            {pagination.totalPages > 1 && (
              <div className="sg-pagination">
                <button
                  className="sg-page-btn"
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrevPage}
                  title="Previous page"
                >
                  <FiChevronLeft size={14} />
                </button>

                {getPageNumbers().map((page, idx) =>
                  page === "..." ? (
                    <span key={`dots-${idx}`} className="sg-page-dots">
                      •••
                    </span>
                  ) : (
                    <button
                      key={page}
                      className={`sg-page-btn ${pagination.currentPage === page ? "sg-page-active" : ""}`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  ),
                )}

                <button
                  className="sg-page-btn"
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNextPage}
                  title="Next page"
                >
                  <FiChevronRight size={14} />
                </button>

                <span className="sg-page-info">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* CREATE/EDIT MODAL */}
      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div
            className="modal-content"
            style={{ height: "auto", minHeight: "auto", maxWidth: 600 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-title">
              <span>
                {editingId ? (
                  <>
                    <FiEdit2 size={16} style={{ marginRight: 8 }} />
                    Edit Suggestion
                  </>
                ) : (
                  <>
                    <FiPlus size={16} style={{ marginRight: 8 }} />
                    New Suggestion
                  </>
                )}
              </span>
              <button className="close-btn" onClick={resetForm}>
                <FiX size={18} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body" style={{ padding: 24 }}>
                <div className="profile-grid">
                  <div className="field">
                    <label>
                      <FiUser size={13} style={{ marginRight: 6 }} />
                      Driver Name
                    </label>
                    <input
                      type="text"
                      placeholder="Enter driver name"
                      value={formData.driverName}
                      onChange={handleDriverNameChange}
                      className={errors.driverName ? "sg-input-error" : ""}
                      required
                    />
                    {errors.driverName && (
                      <span className="sg-error-text">
                        <FiAlertCircle size={11} /> {errors.driverName}
                      </span>
                    )}
                    <span className="sg-input-hint">
                      Letters only, auto-capitalized
                    </span>
                  </div>
                  <div className="field">
                    <label>
                      <FiTruck size={13} style={{ marginRight: 6 }} />
                      Vehicle Number
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. TN72AB1234"
                      value={formData.vehicleNumber}
                      onChange={handleVehicleChange}
                      className={errors.vehicleNumber ? "sg-input-error" : ""}
                      style={{ textTransform: "uppercase" }}
                      required
                    />
                    {errors.vehicleNumber && (
                      <span className="sg-error-text">
                        <FiAlertCircle size={11} /> {errors.vehicleNumber}
                      </span>
                    )}
                    <span className="sg-input-hint">
                      Uppercase, no spaces or special characters
                    </span>
                  </div>
                  <div className="field">
                    <label>
                      <FiPhone size={13} style={{ marginRight: 6 }} />
                      Mobile Number
                    </label>
                    <div className="sg-mobile-input-wrap">
                      <span className="sg-country-code">+91</span>
                      <input
                        type="text"
                        placeholder="9876543210"
                        value={formData.mobileNumber}
                        onChange={handleMobileChange}
                        className={errors.mobileNumber ? "sg-input-error" : ""}
                        inputMode="numeric"
                        maxLength={10}
                        required
                      />
                    </div>
                    {errors.mobileNumber && (
                      <span className="sg-error-text">
                        <FiAlertCircle size={11} /> {errors.mobileNumber}
                      </span>
                    )}
                    <div className="sg-input-hint-row">
                      <span className="sg-input-hint">
                        10 digits only, starts with 6-9
                      </span>
                      <span
                        className={`sg-char-count ${formData.mobileNumber.length === 10 ? "sg-count-valid" : ""}`}
                      >
                        {formData.mobileNumber.length}/10
                      </span>
                    </div>
                  </div>
                  <div className="field">
                    <label>
                      <FiUser size={13} style={{ marginRight: 6 }} />
                      Created By
                    </label>
                    <input
                      disabled
                      type="text"
                      value={formData.createdBy}
                      onChange={handleCreatedByChange}
                      className={errors.createdBy ? "sg-input-error" : ""}
                      required
                    />
                    {errors.createdBy && (
                      <span className="sg-error-text">
                        <FiAlertCircle size={11} /> {errors.createdBy}
                      </span>
                    )}
                    <span className="sg-input-hint">
                      Auto-filled from admin profile
                    </span>
                  </div>
                </div>
                <div className="field" style={{ marginTop: 16 }}>
                  <label>
                    <FiMessageSquare size={13} style={{ marginRight: 6 }} />
                    Suggestion
                  </label>
                  <textarea
                    placeholder="Write your suggestion here..."
                    value={formData.suggestion}
                    onChange={(e) => {
                      setFormData({ ...formData, suggestion: e.target.value });
                      if (errors.suggestion)
                        setErrors({ ...errors, suggestion: "" });
                    }}
                    className={errors.suggestion ? "sg-input-error" : ""}
                    required
                    rows={4}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: `1px solid ${errors.suggestion ? "#a32d2d" : "#ddd"}`,
                      borderRadius: 8,
                      fontSize: 13,
                      fontFamily: "inherit",
                      resize: "vertical",
                      outline: "none",
                      transition: "border 0.15s",
                    }}
                    onFocus={(e) =>
                      (e.target.style.borderColor = errors.suggestion
                        ? "#a32d2d"
                        : "#1d9e75")
                    }
                    onBlur={(e) =>
                      (e.target.style.borderColor = errors.suggestion
                        ? "#a32d2d"
                        : "#ddd")
                    }
                  />
                  {errors.suggestion && (
                    <span className="sg-error-text">
                      <FiAlertCircle size={11} /> {errors.suggestion}
                    </span>
                  )}
                </div>
                <div className="field" style={{ marginTop: 16 }}>
                  <label>
                    <FiCheckCircle size={13} style={{ marginRight: 6 }} />
                    Decision Status
                  </label>
                  <div className="sg-decision-toggle">
                    <button
                      type="button"
                      className={`sg-toggle-btn sg-toggle-yes ${formData.isAccepted === true ? "sg-toggle-active-yes" : ""}`}
                      onClick={() =>
                        setFormData({ ...formData, isAccepted: true })
                      }
                    >
                      <FiThumbsUp size={14} />
                      <span>Accepted</span>
                    </button>
                    <button
                      type="button"
                      className={`sg-toggle-btn sg-toggle-pending ${formData.isAccepted === null ? "sg-toggle-active-pending" : ""}`}
                      onClick={() =>
                        setFormData({ ...formData, isAccepted: null })
                      }
                    >
                      <FiClock size={14} />
                      <span>Pending</span>
                    </button>
                    <button
                      type="button"
                      className={`sg-toggle-btn sg-toggle-no ${formData.isAccepted === false ? "sg-toggle-active-no" : ""}`}
                      onClick={() =>
                        setFormData({ ...formData, isAccepted: false })
                      }
                    >
                      <FiThumbsDown size={14} />
                      <span>Rejected</span>
                    </button>
                  </div>
                  <span className="sg-input-hint">
                    Set initial decision or leave as pending
                  </span>
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-outline-green"
                  onClick={resetForm}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <div className="spinner"></div>
                      {editingId ? "Updating..." : "Submitting..."}
                    </>
                  ) : editingId ? (
                    <>
                      <FiEdit2 size={14} /> Update Suggestion
                    </>
                  ) : (
                    <>
                      <FiPlus size={14} /> Add Suggestion
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirm.show && (
        <div
          className="modal-overlay"
          onClick={() =>
            !deciding && setShowConfirm({ show: false, id: null, action: null })
          }
        >
          <div
            className="modal-content"
            style={{
              height: "auto",
              minHeight: "auto",
              maxWidth: 420,
              textAlign: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-body" style={{ padding: "40px 30px" }}>
              <div
                className="sg-confirm-icon"
                style={{
                  background:
                    showConfirm.action === "approve" ? "#E1F5EE" : "#FCEBEB",
                  color:
                    showConfirm.action === "approve" ? "#0F6E56" : "#A32D2D",
                }}
              >
                {showConfirm.action === "approve" ? (
                  <FiThumbsUp size={32} />
                ) : (
                  <FiThumbsDown size={32} />
                )}
              </div>
              <h3
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  marginTop: 16,
                  color: "#1a1a1a",
                }}
              >
                {showConfirm.action === "approve"
                  ? "Approve this suggestion?"
                  : "Reject this suggestion?"}
              </h3>
              <p style={{ fontSize: 13, color: "#888", marginTop: 8 }}>
                {showConfirm.action === "approve"
                  ? "This will be marked as accepted."
                  : "This will be marked as rejected."}
              </p>
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  justifyContent: "center",
                  marginTop: 24,
                }}
              >
                <button
                  className="btn-outline-green-green"
                  disabled={deciding}
                  onClick={() =>
                    setShowConfirm({ show: false, id: null, action: null })
                  }
                >
                  Cancel
                </button>
                <button
                  className={`btn ${showConfirm.action === "approve" ? "btn-success" : "btn-danger"}`}
                  disabled={deciding}
                  onClick={() =>
                    handleDecision(
                      showConfirm.id,
                      showConfirm.action === "approve",
                    )
                  }
                >
                  {deciding ? (
                    <>
                      <div className="spinner"></div> Processing...
                    </>
                  ) : showConfirm.action === "approve" ? (
                    <>
                      <FiThumbsUp size={14} /> Yes, Approve
                    </>
                  ) : (
                    <>
                      <FiThumbsDown size={14} /> Yes, Reject
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
